/**
 * m3 echo Frontend - Billing API Client
 *
 * API client for billing module management and tenant module assignments.
 */

import apiClient from './api';

// ===========================
// Type Definitions
// ===========================

export interface BillingModule {
  id: number;
  name: string;
  display_name: string;
  description: string | null;
  module_type: 'quota' | 'model' | 'feature' | 'custom';
  grant_config: Record<string, any>;
  base_price: number;
  grace_period_days: number;
  is_active: boolean;
  assignment_count: number;
  created_at: string;
  updated_at: string;
}

export interface BillingModuleCreate {
  name: string;
  display_name: string;
  description?: string;
  module_type: 'quota' | 'model' | 'feature' | 'custom';
  grant_config: Record<string, any>;
  base_price: number;
  grace_period_days: number;
}

export interface BillingModuleUpdate {
  display_name?: string;
  description?: string;
  base_price?: number;
  grace_period_days?: number;
  is_active?: boolean;
}

export interface TenantModuleAssignment {
  id: number;
  tenant_id: number;
  module_id: number;
  module_name: string;
  module_display_name: string;
  module_type: string;
  base_price: number;
  grant_config: Record<string, any>;
  active_from: string;
  active_until: string | null;
  billing_from: string | null;
  billing_until: string | null;
  discount_percent: number;
  effective_price: number;
  notes: string | null;
  is_currently_active: boolean;
  is_in_grace_period: boolean;
  status: 'aktiv' | 'kulanz' | 'abgelaufen' | 'geplant';
  created_at: string;
  updated_at: string;
}

export interface TenantModuleCreate {
  module_id: number;
  active_from: string;
  active_until?: string | null;
  billing_from?: string | null;
  billing_until?: string | null;
  discount_percent: number;
  notes?: string;
}

export interface TenantModuleUpdate {
  active_from?: string;
  active_until?: string | null;
  billing_from?: string | null;
  billing_until?: string | null;
  discount_percent?: number;
  notes?: string;
}

export interface TenantEffectiveConfig {
  tenant_id: number;
  effective_quota: number;
  optimization_run_quota: number;
  optimization_runs_used: number;
  optimization_runs_remaining: number;
  active_models: string[];
  active_features: Record<string, boolean>;
  monthly_cost: number;
}

export interface BillingOverviewTenant {
  id: number;
  name: string;
  display_name: string;
  active_module_count: number;
  monthly_cost: number;
}

export interface EbitMonth {
  month: string;
  revenue: number;
  costs: number;          // Tenant token costs
  system_costs: number;   // Shared system token costs
  total_costs: number;    // costs + system_costs
  ebit: number;           // revenue - total_costs
}

export interface EbitTenant {
  id: number;
  name: string;
  display_name: string;
}

/** Exchange rate the backend used to express token costs in the reporting currency. */
export interface FxInfo {
  source_currency: string;   // Currency the model prices are stored in
  target_currency: string;   // Reporting currency
  rate: number;
  rate_date: string | null;  // null = no documented reference date
  is_default_rate: boolean;  // true = no rate configured, amounts are unconverted
}

/** Usage the price history could not cover; those records contribute 0. */
export interface PricingGaps {
  models: string[];
  records: number;
}

export interface EbitData {
  months: EbitMonth[];
  tenants: EbitTenant[];
  /** Currency every money value in `months` is expressed in. */
  currency: string;
  fx: FxInfo;
  pricing_gaps: PricingGaps;
}

// ===========================
// API
// ===========================

export const billingApi = {
  // Module CRUD
  getModules: () =>
    apiClient.get<BillingModule[]>('/admin/billing/modules'),

  createModule: (data: BillingModuleCreate) =>
    apiClient.post<BillingModule>('/admin/billing/modules', data),

  updateModule: (id: number, data: BillingModuleUpdate) =>
    apiClient.put<BillingModule>(`/admin/billing/modules/${id}`, data),

  deleteModule: (id: number) =>
    apiClient.delete(`/admin/billing/modules/${id}`),

  // Tenant Module Assignments
  getTenantModules: (tenantId: number) =>
    apiClient.get<TenantModuleAssignment[]>(`/admin/billing/tenants/${tenantId}/modules`),

  assignModule: (tenantId: number, data: TenantModuleCreate) =>
    apiClient.post<TenantModuleAssignment>(`/admin/billing/tenants/${tenantId}/modules`, data),

  updateAssignment: (assignmentId: number, data: TenantModuleUpdate) =>
    apiClient.put<TenantModuleAssignment>(`/admin/billing/assignments/${assignmentId}`, data),

  deleteAssignment: (assignmentId: number) =>
    apiClient.delete(`/admin/billing/assignments/${assignmentId}`),

  // Computed
  getEffectiveConfig: (tenantId: number) =>
    apiClient.get<TenantEffectiveConfig>(`/admin/billing/tenants/${tenantId}/effective-config`),

  getOverview: () =>
    apiClient.get<BillingOverviewTenant[]>('/admin/billing/overview'),

  // EBIT
  getEbit: (tenantId?: number) =>
    apiClient.get<EbitData>('/admin/billing/ebit', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),

  // User-facing (non-admin)
  getMyConfig: () =>
    apiClient.get<TenantEffectiveConfig>('/billing/my-config'),
};
